import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../auth/AuthContext';
import { claimGuestRun, readLocalBoards, isDayClaimed, markDayClaimed } from '../api/anon';
import {
  loadLocalLife,
  claimLivesFrom,
  hasAnyLocalSave,
  type ClaimLifeInput,
} from '../game/local-persistence';
import { fetchTodayPuzzles, isLockedPuzzle, type Difficulty } from '../api/puzzles';
import { londonToday } from '../lib/gameDate';

/**
 * When a session appears and a claimable guest day is sitting in localStorage,
 * hand it to the server once. Covers all three entry paths: register → verify,
 * Google callback, and an existing user logging in after playing as a guest.
 *
 * The save is only marked claimed on a definite server answer — a network
 * failure leaves it (and the local save) in place for the next session to retry.
 */
export function useClaimGuestRun(): void {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const running = useRef(false);

  useEffect(() => {
    if (loading || !user || running.current) return;

    const date = londonToday();
    if (isDayClaimed(date)) return;

    // Nothing saved locally at all means nothing claimable — skip the
    // network round trip to /api/puzzles/today that every other check below
    // depends on. This is the overwhelmingly common case for a returning,
    // signed-in player with no guest run sitting around.
    if (!hasAnyLocalSave()) return;

    running.current = true;
    void (async () => {
      try {
        const today = await fetchTodayPuzzles();

        // Fast path: the mirror (written on claimBoard's success path) names
        // the committed difficulty directly.
        const mirrored = readLocalBoards(date)[0];
        let difficulty: Difficulty | undefined = mirrored;
        let lives: ClaimLifeInput[] | null = null;
        if (mirrored) {
          const puzzle = today.puzzles.find((p) => p.difficulty === mirrored);
          if (puzzle && !isLockedPuzzle(puzzle)) {
            lives = claimLivesFrom(loadLocalLife(puzzle.id));
          }
        }

        // The mirror is a rendering hint only, written solely when
        // claimBoard's POST succeeds — and claimBoard deliberately fails
        // open, so an unreachable /api/anon/board leaves the mirror empty
        // while the guest keeps playing normally. Fall back to scanning
        // today's puzzles for whichever one has a claimable local save, so
        // that failure mode doesn't silently drop the run at signup.
        if (!lives) {
          for (const puzzle of today.puzzles) {
            if (isLockedPuzzle(puzzle)) continue;
            const candidate = claimLivesFrom(loadLocalLife(puzzle.id));
            if (candidate) {
              difficulty = puzzle.difficulty;
              lives = candidate;
              break;
            }
          }
        }

        if (!difficulty || !lives) return;

        const result = await claimGuestRun({ date, difficulty, lives });
        markDayClaimed(date);

        if (result.claimed) {
          toast.success(
            result.rank
              ? `Your ${result.best_score} is on today's board — #${result.rank}.`
              : 'Your guest score has been added to your account.',
          );
          void queryClient.invalidateQueries({ queryKey: ['me'] });
          void queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
          // The claim replays the guest's whole day into real ended lives, so
          // the player's season points jump from nothing to a full day's
          // worth. Without this the home strip still reads "Finish a board to
          // start your season" right after signing in.
          void queryClient.invalidateQueries({ queryKey: ['seasons', 'current'] });
        } else if (result.reason === 'ALREADY_PLAYED') {
          toast(
            `Your account already has today's ${difficulty.toLowerCase()} board — your guest score wasn't added.`,
          );
        }
      } catch {
        // Leave the save (and the claimed flag unset) in place; the next session retries.
      } finally {
        running.current = false;
      }
    })();
  }, [user, loading, queryClient]);
}
